import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IoAlertCircleOutline, IoCheckmark, IoMailOutline } from "react-icons/io5";
import HttpApis from "../../services/HttpApis";
import { useTenant } from "../../TenantContext";
import "./Unsubscribe.css";

// Landing page for the "Unsubscribe" link in marketing emails (new-release
// alerts). Deliberately does NOT call the API on mount — mail-client link
// scanners (Outlook Safe Links etc.) prefetch this page's GET, and if loading
// it unsubscribed people automatically, scanners would silently unsubscribe
// everyone before a human ever saw it. Only the button click mutates anything.
export default function Unsubscribe() {
  // Same name the "New on <app>" emails use, so the page matches the email.
  // Tenant-resolved, not from env: this app serves many tenants from one
  // deployment, so a module-level REACT_APP_SITE_TITLE would show the wrong
  // brand (and that var is not set at all, so it showed none). Same pattern as
  // PageTopBar / Footer / FilterPage.
  const { tenant } = useTenant();
  const siteTitle = tenant?.name || process.env.REACT_APP_SITE_TITLE;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [status, setStatus] = useState(token ? "confirm" : "invalid");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const readError = (err) =>
    err?.response?.data?.message || "This unsubscribe link is no longer valid.";

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await HttpApis.unsubscribe({ token });
      setStatus("success");
    } catch (err) {
      setMessage(readError(err));
      setStatus("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubscribe = async () => {
    setSubmitting(true);
    try {
      await HttpApis.resubscribe({ token });
      setStatus("resubscribed");
    } catch (err) {
      setMessage(readError(err));
      setStatus("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  const homeLink = (
    <Link to="/" className="unsubscribe-btn">
      Go to homepage
    </Link>
  );

  return (
    <div className="unsubscribe-container">
      <div className="unsubscribe-card">
        {status === "confirm" && (
          <>
            <div className="unsubscribe-icon unsubscribe-icon--confirm">
              <IoMailOutline />
            </div>
            <h1>Unsubscribe from new&#8209;release emails?</h1>
            <p>
              {siteTitle
                ? `You'll stop receiving "New on ${siteTitle}" content alerts.`
                : "You'll stop receiving new-release content alerts."}{" "}
              This won't affect your account or password-reset emails.
            </p>
            <div className="unsubscribe-actions">
              <button
                className="unsubscribe-btn"
                onClick={handleConfirm}
                disabled={submitting}
              >
                {submitting ? "Unsubscribing…" : "Confirm Unsubscribe"}
              </button>
              <button
                className="unsubscribe-btn unsubscribe-btn--secondary"
                onClick={() => navigate("/")}
                disabled={submitting}
              >
                Keep me subscribed
              </button>
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <div className="unsubscribe-icon unsubscribe-icon--success">
              <IoCheckmark />
            </div>
            <h1>You're unsubscribed</h1>
            <p>You won't receive new-release emails anymore.</p>
            <div className="unsubscribe-actions">
              {homeLink}
              <button
                className="unsubscribe-btn unsubscribe-btn--secondary"
                onClick={handleResubscribe}
                disabled={submitting}
              >
                {submitting ? "Resubscribing…" : "Resubscribe"}
              </button>
            </div>
          </>
        )}

        {status === "resubscribed" && (
          <>
            <div className="unsubscribe-icon unsubscribe-icon--success">
              <IoCheckmark />
            </div>
            <h1>You're subscribed again</h1>
            <p>You'll keep getting new-release emails.</p>
            <div className="unsubscribe-actions">{homeLink}</div>
          </>
        )}

        {status === "invalid" && (
          <>
            <div className="unsubscribe-icon unsubscribe-icon--invalid">
              <IoAlertCircleOutline />
            </div>
            <h1>Link expired or invalid</h1>
            <p>{message || "This unsubscribe link is no longer valid."}</p>
            <div className="unsubscribe-actions">{homeLink}</div>
          </>
        )}
      </div>
    </div>
  );
}
